import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useForm } from "react-hook-form";
import type { DateValueType } from "react-tailwindcss-datepicker";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import Autocomplete from "@/components/Autocomplete";
import TextField from "@/components/TextField";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { GET_MANAGEABLE_SECTIONS } from "@/pages/events/queries";
import {
  ACHIEVEMENTS_FOR_MEMBER_QUERY,
  findAchievementTypeForMember,
  type MembershipAchievementCategoryEnum,
  type MembershipAchievementForMember,
  type MembershipAchievementType
} from "@/pages/people/[pgaId]/queries";
import {
  UPSERT_ACHIEVEMENT_ATTRIBUTE_MUTATION,
  UPSERT_ACHIEVEMENT_CODE_MUTATION,
  UPSERT_NATIVE_ACHIEVEMENT_MUTATION
} from "@/pages/people/[pgaId]/mutations";
import AchievementTypeField from "./components/AchievementTypeField";
import ControllerField from "./components/ControllerField";
import {
  buildEditFormValues,
  buildSectionOptions,
  DEFAULT_CAPABILITIES,
  defaultExpiresOnFromRecognition,
  emptyFormValues,
  getAchievementStrategy,
  getCategoryLabel,
  getRecognizedByFormName,
  saveAchievement,
  sectionsForOptions
} from "./utils";
import type { EditAchievementFormValues, SectionOptionSource } from "./types";

type ManageableSectionsQueryData = {
  manageableSections: {
    nodes: SectionOptionSource[];
  };
};

export { buildAchievementTypeOptions } from "./utils";

const SECTION_SEARCH_DEBOUNCE_MS = 300;

interface EditAchievementModalProps {
  achievement: MembershipAchievementForMember | null;
  category: MembershipAchievementCategoryEnum;
  mode: "add" | "edit";
  onClose(): void;
  open: boolean;
  pgaId: string;
}

export default function EditAchievementModal({
  achievement,
  category,
  mode,
  onClose,
  open,
  pgaId
}: EditAchievementModalProps) {
  const isAddMode = mode === "add";
  const categoryLabel = getCategoryLabel(category);
  const [achievementTypeLabel, setAchievementTypeLabel] = useState("");
  const [achievementTypesLoading, setAchievementTypesLoading] = useState(false);
  const [achievementTypes, setAchievementTypes] = useState<MembershipAchievementType[]>([]);
  const [sectionQuery, setSectionQuery] = useState("");
  const [debouncedSectionQuery, setDebouncedSectionQuery] = useState("");
  const editFormInitializedForIdRef = useRef<string | null>(null);

  const refetchAchievements = {
    refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
    awaitRefetchQueries: true
  };

  const [upsertAchievementCode, { loading: savingCode }] = useMutation(
    UPSERT_ACHIEVEMENT_CODE_MUTATION,
    refetchAchievements
  );
  const [upsertAchievementAttribute, { loading: savingAttribute }] = useMutation(
    UPSERT_ACHIEVEMENT_ATTRIBUTE_MUTATION,
    refetchAchievements
  );
  const [upsertNativeAchievement, { loading: savingNative }] = useMutation(
    UPSERT_NATIVE_ACHIEVEMENT_MUTATION,
    refetchAchievements
  );

  const { control, handleSubmit, reset, setValue, watch } = useForm<EditAchievementFormValues>({
    defaultValues: emptyFormValues
  });

  const strategy = getAchievementStrategy(watch("native"), watch("ccmsCodeType"), watch("ccmsAttributeType"));
  const capabilities = strategy?.capabilities ?? DEFAULT_CAPABILITIES;
  const needsSectionOptions = Boolean(strategy?.sectionValueKey);
  const sectionValueKey = strategy?.sectionValueKey ?? null;
  const recognizedAt = watch("recognizedAt");
  const selectedAchievementType = achievementTypes.find(
    (achievementType) => achievementType.id === watch("achievementTypeId")
  );
  const expiresOnRequired = Boolean(selectedAchievementType?.defaultExpirationDays);
  const recognizedByFormName = getRecognizedByFormName(sectionValueKey);
  const sectionLegacyId = watch("sectionLegacyId");
  const managingSectionId = watch("managingSectionId");
  const recognizedByValue = sectionValueKey === "id" ? managingSectionId : sectionLegacyId;
  const trimmedSectionSearch = debouncedSectionQuery.trim();

  const { data: sectionsData, loading: sectionsLoading } = useQuery<ManageableSectionsQueryData>(
    GET_MANAGEABLE_SECTIONS,
    {
      fetchPolicy: "cache-first",
      skip: !open || !needsSectionOptions,
      variables: { input: { search: trimmedSectionSearch || null } }
    }
  );

  const manageableSections = sectionsData?.manageableSections?.nodes;
  const sectionOptions = buildSectionOptions(
    sectionsForOptions(manageableSections, isAddMode ? null : achievement?.recognizingEntity),
    sectionValueKey ?? "legacyId"
  );
  const saving = savingCode || savingAttribute || savingNative;
  const disableRecognizedAt = !isAddMode && Boolean(strategy?.lockRecognizedAtOnEdit);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSectionQuery(recognizedByValue ? "" : sectionQuery);
    }, SECTION_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [recognizedByValue, sectionQuery]);

  useEffect(() => {
    if (!open || !isAddMode) {
      return;
    }

    reset(emptyFormValues);
    setAchievementTypeLabel("");
    setSectionQuery("");
    setDebouncedSectionQuery("");
  }, [isAddMode, open, reset]);

  useEffect(() => {
    if (!open || !isAddMode) {
      return;
    }

    setValue(
      "expiresOn",
      defaultExpiresOnFromRecognition(recognizedAt, selectedAchievementType?.defaultExpirationDays)
    );
  }, [isAddMode, open, recognizedAt, selectedAchievementType?.defaultExpirationDays, setValue]);

  useEffect(() => {
    if (
      !open ||
      !sectionValueKey ||
      sectionsLoading ||
      trimmedSectionSearch ||
      sectionOptions.length !== 1 ||
      recognizedByValue
    ) {
      return;
    }

    const [onlyOption] = sectionOptions;
    setValue(recognizedByFormName, onlyOption.value);
    setSectionQuery(onlyOption.label);
  }, [
    open,
    recognizedByFormName,
    recognizedByValue,
    sectionOptions,
    sectionValueKey,
    sectionsLoading,
    setValue,
    trimmedSectionSearch
  ]);

  useEffect(() => {
    if (!open) {
      editFormInitializedForIdRef.current = null;
      return;
    }

    if (isAddMode || !achievement) {
      return;
    }

    if (editFormInitializedForIdRef.current === achievement.id) {
      return;
    }

    const matchingAchievementType = findAchievementTypeForMember(achievementTypes, achievement);
    if (!matchingAchievementType) {
      return;
    }

    const { typeLabel, values } = buildEditFormValues({
      achievement,
      matchingAchievementType,
      sections: manageableSections
    });

    reset(values);
    setAchievementTypeLabel(typeLabel);
    setSectionQuery(achievement.recognizingEntity?.name ?? "");
    editFormInitializedForIdRef.current = achievement.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achievement, achievementTypes, isAddMode, open, reset]);

  const handleClose = () => {
    reset(emptyFormValues);
    setAchievementTypeLabel("");
    setSectionQuery("");
    setDebouncedSectionQuery("");
    onClose();
  };

  const onSubmit = async (values: EditAchievementFormValues) => {
    const failureMessage = `Unable to ${isAddMode ? "add" : "update"} ${categoryLabel.toLowerCase()}.`;

    try {
      const result = await saveAchievement({
        values,
        pgaId,
        categoryLabel,
        achievementId: isAddMode ? null : achievement?.id,
        mutations: {
          upsertNativeAchievement,
          upsertAchievementCode,
          upsertAchievementAttribute
        }
      });

      if (!result.success) {
        showErrorSnackbar(result.message || failureMessage);
        return;
      }

      showSnackbar(`${categoryLabel} ${isAddMode ? "added" : "updated"}`);
      handleClose();
    } catch {
      showErrorSnackbar(failureMessage);
    }
  };

  if (!open || (!isAddMode && !achievement)) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline={`${isAddMode ? "Add" : "Edit"} ${categoryLabel}`}
      className="w-[640px] max-h-[85vh]"
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={saving || achievementTypesLoading || (needsSectionOptions && sectionsLoading)}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <input type="hidden" name="id" value={achievement?.id} />
        <AchievementTypeField
          category={category}
          categoryLabel={categoryLabel}
          control={control}
          disabled={!isAddMode}
          initialTypeLabel={achievementTypeLabel}
          isAddMode={isAddMode}
          onAchievementTypesChange={setAchievementTypes}
          onLoadingChange={setAchievementTypesLoading}
          open={open}
          setValue={setValue}
        />

        <ControllerField
          name="title"
          capabilities={capabilities}
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Title"
              value={value}
              onChangeText={onChange}
              className="w-full"
              data-testid={`edit-${categoryLabel.toLowerCase()}-title`}
            />
          )}
        />

        <ControllerField
          name="recognizedAt"
          capabilities={capabilities}
          control={control}
          rules={{ required: "Recognized At is required" }}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <DateSelect
              label="Recognized At *"
              value={{ startDate: value || null, endDate: value || null }}
              onChange={(dateValue: DateValueType | string) => {
                if (typeof dateValue === "string") return;
                onChange(dateValue?.startDate ?? "");
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              disabled={disableRecognizedAt}
              error={!!error}
              errorMessage={error?.message}
            />
          )}
        />

        <ControllerField
          name="recognitionPeriod"
          capabilities={capabilities}
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Recognition Period"
              type="number"
              min={1900}
              step={1}
              placeholder="YYYY"
              value={value}
              onChangeText={onChange}
              className="w-full"
              data-testid="edit-achievement-recognition-period"
            />
          )}
        />

        <ControllerField
          name="expiresOn"
          capabilities={capabilities}
          control={control}
          rules={{ required: expiresOnRequired ? "Expires On is required" : false }}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <DateSelect
              label={expiresOnRequired ? "Expires On *" : "Expires On"}
              value={{ startDate: value || null, endDate: value || null }}
              onChange={(dateValue: DateValueType | string) => {
                if (typeof dateValue === "string") return;
                onChange(dateValue?.startDate ?? "");
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              error={!!error}
              errorMessage={error?.message}
            />
          )}
        />

        <ControllerField
          name="recognizedBy"
          formName={recognizedByFormName}
          capabilities={capabilities}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              variant="searchableDropdown"
              label="Recognized By"
              data={sectionOptions}
              query={sectionQuery}
              loadingResults={sectionsLoading}
              placeholder="Search sections"
              data-testid="edit-award-recognized-by"
              constrainMenuWidth
              menuClassName="max-h-[300px]"
              renderItem={(option) => <div className="text-body-large">{option.label}</div>}
              onChangeText={(text) => {
                setSectionQuery(text);
                if (value) onChange("");
              }}
              reset={() => {
                setSectionQuery("");
                setDebouncedSectionQuery("");
                onChange("");
              }}
              onSelect={(option) => {
                onChange(option.value);
                setSectionQuery(option.label);
                setDebouncedSectionQuery("");
              }}
            />
          )}
        />

        <ControllerField
          name="remarks"
          capabilities={capabilities}
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Remarks"
              value={value}
              onChangeText={onChange}
              className="w-full"
              data-testid="edit-award-remarks"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
